import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { DSSetup, Service } from 'webapp-types';

import didUpdate from 'webapp/modifiers/did-update';
import BarLoaderComponent from 'webapp/components/bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { MetricInsightsChartState } from 'webapp/components/metric-details/explore/data';
import MetricDetailsExploreRelatedMetricsPreviousPeriodComponent from 'webapp/components/metric-details/explore/related-metrics/previous-period';

import type DateRange from 'webapp/models-custom/date-range';
import MetricSetup from 'webapp/models-custom/metric-setup';

interface Args {
	chartState: MetricInsightsChartState;
	metricSetup: MetricSetup;
	dateRange: DateRange | null;
	forceLoadingState: boolean;
	origin: string;
}

export default class MetricDetailsExploreRelatedMetricsIndexComponent extends Component<Args> {
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked private _relatedMetricSetupData: DSSetup.MetricSetupWithoutFiltersData[] = [];
	@tracked isLoading = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		void this.loadRelatedMetrics();
	}

	get source() {
		return this.args.metricSetup.source;
	}

	get sourceType() {
		const sourceType = this.source?.sourceType;

		return sourceType;
	}

	async loadRelatedMetrics() {
		const metricSetupData = this.args.metricSetup.metricSetupWithoutFiltersData;
		this.isLoading = true;
		if (metricSetupData) {
			try {
				const relatedMetricSetupData = await this.mmMetricsService.getCorrelatedMetrics(
					metricSetupData,
					3,
				);
				this._relatedMetricSetupData = relatedMetricSetupData || [];
			} catch (_error) {
				this._relatedMetricSetupData = [];
			}
		}
		this.isLoading = false;
	}

	@cached
	get relatedMetricSetups(): MetricSetup[] {
		if (this.isLoading) {
			return [];
		}

		const relatedMetricSetups: MetricSetup[] = [];
		this._relatedMetricSetupData.forEach((metricSetupData) => {
			const metricSetup = new MetricSetup(metricSetupData);
			if (metricSetup.metric) {
				relatedMetricSetups.push(metricSetup);
			}
		});

		relatedMetricSetups.sort((a, b) => (b.metric!.usedCount || 0) - (a.metric!.usedCount || 0));

		return relatedMetricSetups;
	}

	@action
	onMetricSelectionChange() {
		void this.loadRelatedMetrics();
	}
	<template>
		<div
			class="metric-explore-related-metrics-wrapper"
			{{didUpdate
				this.onMetricSelectionChange
				@metricSetup.key
				@metricSetup.spaceAccessId
				@metricSetup.dimensions
			}}
		>
			{{#if this.isLoading}}
				<div class="related-metrics-loader-wrapper module-box">
					<BarLoaderComponent />
				</div>
			{{else if this.relatedMetricSetups}}
				<div class="module-box">
					<div class="module-box__header">
						<div class="module-box__title">
							<CommonUiIconComponent @name="MetricDetail" @size="md" />
							<span>Explore metrics that add context to this metric's performance</span>
						</div>
					</div>
					<div class="module-box__divider" />
					<div class="metric-explore-related-metrics">
						{{#each this.relatedMetricSetups as |metricSetup|}}
							<MetricDetailsExploreRelatedMetricsPreviousPeriodComponent
								@model={{metricSetup}}
								@chartState={{@chartState}}
								@dateRange={{@dateRange}}
								@forceLoadingState={{@forceLoadingState}}
								@origin={{@origin}}
							/>
						{{/each}}
					</div>
				</div>
			{{/if}}
		</div>
	</template>
}
